import { useQuery } from "@tanstack/react-query";
import type { Nurse, NurseInShift } from "../../../utils/models";
import { fetchPtoDeletedShifts } from "../downloadScheduleUtils";

const PTO_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Stable identity for the disabled case. An inline `{}` would be a new object every render, and this
 * record is a PDF document prop — a fresh identity rebuilds the document forever.
 */
const EMPTY_PTO: Record<string, NurseInShift[]> = Object.freeze({});

type Params = {
	departmentId: string;
	/** `yyyy-MM-dd`. */
	startDate: string;
	/** `yyyy-MM-dd`. */
	endDate: string;
	nurses: Nurse[];
	/** The department's name-order flag (`useLastNameFirst()`); the PTO rows carry the formatted name. */
	lastNameFirst: boolean;
	/** False unless the popup is open on the weekly tab with the PTO sheet setting on. */
	enabled: boolean;
};

/**
 * The PTO-removal rows the weekly printout overlays, grouped by date.
 *
 * Grouping happens inside `queryFn` on purpose, so the CACHED value is the grouped record and its
 * identity is stable until a real refetch. Grouping in a `select` (re-runs per render) or a
 * `useMemo` over the array would hand the PDF a new object on every render and rebuild it endlessly.
 */
export const usePtoDeletedShiftsByDate = ({ departmentId, startDate, endDate, nurses, lastNameFirst, enabled }: Params) => {
	// `nurses` is in the key because `fetchPtoDeletedShifts` DROPS any shift whose nurse it cannot find:
	// opening the popup before `refreshNursesData` resolves would otherwise cache an empty PTO sheet for
	// the whole stale window. The count is enough — it is the list arriving that matters, not its content.
	const query = useQuery<Record<string, NurseInShift[]>>({
		queryKey: ["pto-deleted-shifts", departmentId, startDate, endDate, nurses.length, lastNameFirst],
		enabled: enabled && nurses.length > 0,
		retry: 1,
		staleTime: PTO_STALE_TIME_MS,
		queryFn: async () => {
			const shifts = await fetchPtoDeletedShifts(departmentId, startDate, endDate, nurses, lastNameFirst);
			const byDate: Record<string, NurseInShift[]> = {};
			for (const shift of shifts) {
				const dateKey = shift.startTime.toFormat("yyyy-MM-dd");
				if (!byDate[dateKey]) byDate[dateKey] = [];
				byDate[dateKey].push(shift);
			}
			return byDate;
		},
	});

	return {
		// Gated on `enabled`, not just on `data`: react-query keeps the last result when a query is
		// disabled, so reading `data` directly meant unticking the setting left the PTO rows in both the
		// preview and the downloaded file for the rest of the session.
		ptoShiftsByDate: enabled ? (query.data ?? EMPTY_PTO) : EMPTY_PTO,
		isLoading: query.isFetching,
		/** The export still works without the PTO rows, so this warns rather than blocks. */
		error: query.error,
	};
};
